// VenueSingle.js - Restructured for different booking types
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  ModalContent,
  OverviewMainTitle,
  VenueContainer,
  ContentContainer,
  StickyContainer,
  PromoContent
} from './styled';

import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getVenue, getVenuePrice, createBooking, getVenueResources } from 'api';
import { Grid, Box, Modal, useMediaQuery, useTheme, Alert } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import Login from 'components/pages/login';
import GalleryGrid from './galleryGrid';
import GeneralInfo from './generalInfo';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { API_BASE_URL, REACT_APP_STRIPE_KEY } from 'config/api';
import { useProfile } from 'context';
import 'react-image-gallery/styles/css/image-gallery.css';

import { BookingCreator } from 'components/organisms/bookingCreator';
import PromoModalContent from './promoModal';
dayjs.locale('en-gb');

// Initialize Stripe OUTSIDE the component to prevent multiple instances
const stripePromise = REACT_APP_STRIPE_KEY ? loadStripe(REACT_APP_STRIPE_KEY) : null;

const VenueSingle = () => {
  const { spaces, bookingType, setBookingType } = useProfile();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { slug } = params;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const prevSelectedResourceRef = useRef(null);
  const hotDeskSectionRef = useRef(null);
  const meetingRoomSectionRef = useRef(null);
  const contentContainerRef = useRef(null);
  const stickyContainerRef = useRef(null);
  const typeChangeShown = useRef(false);

  // URL parameters
  const dateParam = searchParams.get('date');
  const bookingTypeParam = searchParams.get('type');
  const communityParam = searchParams.get('community');
  const previewCode = searchParams.get('code');

  // Auth states
  const [loggedIn, setLoggedIn] = useState(localStorage.getItem('token') != null);
  const [requestLogin, setRequestLogin] = useState(false);
  const [defaulView, setDefaultView] = useState('signup');
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectedCommunity] = useState(communityParam || null);

  // Venue information
  const [venue, setVenue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [venueLoaded, setVenueLoaded] = useState(false);
  const [center, setCenter] = useState(null);
  const [images, setImages] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [openingHoursData, setOpeningHours] = useState([
    { day: 'Monday', time: 'Closed' },
    { day: 'Tuesday', time: 'Closed' },
    { day: 'Wednesday', time: 'Closed' },
    { day: 'Thursday', time: 'Closed' },
    { day: 'Friday', time: 'Closed' },
    { day: 'Saturday', time: 'Closed' },
    { day: 'Sunday', time: 'Closed' },
  ]);

  // Booking states
  const [selectedDate, setSelectedDate] = useState(dateParam ? dayjs(dateParam) : null);
  const [preparingBooking, setPreparingBooking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceToView, setResourceToView] = useState(null);

  // Resources by type
  const [meetingRooms, setMeetingRooms] = useState([]);
  const [hotDeskAreas, setHotDeskAreas] = useState([]);
  const [privateOffices, setPrivateOffices] = useState([]);

  // Loading states for different resource types
  const [loadingMeetingRooms, setLoadingMeetingRooms] = useState(false);
  const [loadingHotDesks, setLoadingHotDesks] = useState(false);

  // Price information
  const [hotDeskPrices, setHotDeskPrices] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPriceVAT, setTotalPriceVAT] = useState(0);

  // Booking type states
  const [enabledHD, setEnabledHD] = useState(false);
  const [enabledMR, setEnabledMR] = useState(false);


  // Track if initial API calls have been made
  const initialLoads = useRef({
    venue: false,
    hotDesks: false,
    meetingRooms: false
  });

  // Helper function to get venue info

  const scrollToStickyPosition = () => {
    // Find the resources section by ID
    const resourcesSection = document.getElementById('resources-section');

    if (resourcesSection) {
      // Calculate the position with offset for header
      const yOffset = -80; // Adjust this based on your header height
      const y = resourcesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;

      // Scroll to that position
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
      return;
    }

    // Fallback to 800 if element not found (shouldn't happen with ID)
    window.scrollTo({
      top: 800,
      behavior: 'smooth'
    });
  };

  const getVenueInfo = () => {
    if (initialLoads.current.venue) {
      return Promise.resolve(null);
    }

    setIsLoading(true);

    const params = previewCode ? { 'code': previewCode } : {};

    return getVenue(slug, params)
      .then(r => {
        initialLoads.current.venue = true;

        const locationCenter = { lat: r?.location[0], lng: r?.location[1] };
        setCenter(locationCenter);
        setVenue(r);

        // Check for active hot desks (in active work areas)
        let hasActiveHotDesks = false;
        // Check for active meeting rooms (regardless of work area status)
        let hasActiveMeetingRooms = false;

        // Process the venue's floors, work areas, and their resources
        if (r.floors && r.floors.length > 0) {
          r.floors.forEach(floor => {
            if (floor.work_areas && floor.work_areas.length > 0) {
              floor.work_areas.forEach(workArea => {
                // Check for active hot desks in active work areas
                if (workArea.status === "AC" &&
                  workArea.hot_desks &&
                  workArea.hot_desks.length > 0) {
                  hasActiveHotDesks = true;
                }

                // Check for active meeting rooms (regardless of work area status)
                if (workArea.meeting_rooms && workArea.meeting_rooms.length > 0) {
                  // Find at least one active meeting room
                  const hasActiveMR = workArea.meeting_rooms.some(room => room.status === "AC");
                  if (hasActiveMR) {
                    hasActiveMeetingRooms = true;
                  }
                }
              });
            }
          });
        }

        // Set state based on our findings
        setEnabledHD(hasActiveHotDesks);
        setEnabledMR(hasActiveMeetingRooms);

        // Now validate the requested booking type from URL
        if (bookingTypeParam) {
          const requestedType = bookingTypeParam.toUpperCase();

          // Check if the requested type is disabled
          if (requestedType === 'HD' && !hasActiveHotDesks) {
            // Hot desks requested but not available

            // Switch to meeting rooms if available, otherwise keep as is
            if (hasActiveMeetingRooms) {
              setBookingType('MR');

              // Show notification
              // Only show notification if we haven't shown it yet
              if (!typeChangeShown.current) {
                enqueueSnackbar(t('venue.hotdesks_not_available'), {
                  variant: 'info',
                });
                typeChangeShown.current = true;
              }

              // Update URL to reflect the change
              const dateQueryParam = selectedDate ? `date=${selectedDate.format('YYYY-MM-DD')}` : '';
              const typeQueryParam = `type=MR`;

              let newURL = `/venue/${slug}?`;
              if (dateQueryParam) {
                newURL += `${dateQueryParam}&${typeQueryParam}`;
              } else {
                newURL += typeQueryParam;
              }

              navigate(newURL, { replace: true });
            }
          } else if (requestedType === 'MR' && !hasActiveMeetingRooms) {
            // Meeting rooms requested but not available

            // Switch to hot desks if available, otherwise keep as is
            if (hasActiveHotDesks) {
              setBookingType('HD');

              // Show notification
              if (!typeChangeShown.current) {
                enqueueSnackbar(t('venue.meetingrooms_not_available'), {
                  variant: 'info',
                });
                typeChangeShown.current = true;
              }

              // Update URL to reflect the change
              const dateQueryParam = selectedDate ? `date=${selectedDate.format('YYYY-MM-DD')}` : '';
              const typeQueryParam = `type=HD`;

              let newURL = `/venue/${slug}?`;
              if (dateQueryParam) {
                newURL += `${dateQueryParam}&${typeQueryParam}`;
              } else {
                newURL += typeQueryParam;
              }

              navigate(newURL, { replace: true });
            }
          }
        } else {
          // No booking type specified in URL - pick an appropriate default
          if (hasActiveHotDesks) {
            setBookingType('HD');
          } else if (hasActiveMeetingRooms) {
            setBookingType('MR');
          } else {
            // Neither is enabled, fall back to venue's available types
            const firstAvailableType = r?.available_booking_types?.[0] || 'HD';
            setBookingType(firstAvailableType);
          }
        }

        // The rest of your existing code for gallery, opening hours, etc.
        if (r.gallery) {
          const galleryImages = r.gallery.map(g => {
            // Use medium for main display, thumbnail for strip, full image for fullscreen
            const imageUrl = g.image?.startsWith('http') ? g.image : (g.image ? `${API_BASE_URL}${g.image}` : null);
            const mediumUrl = g.medium?.startsWith('http') ? g.medium : (g.medium ? `${API_BASE_URL}${g.medium}` : imageUrl);
            const thumbnailUrl = g.thumbnail?.startsWith('http') ? g.thumbnail : (g.thumbnail ? `${API_BASE_URL}${g.thumbnail}` : mediumUrl);
            return {
              original: mediumUrl,      // Medium for main gallery view (faster loading)
              thumbnail: thumbnailUrl,  // Small thumbnail for the strip
              fullscreen: imageUrl,     // Full resolution for fullscreen mode
            };
          });
          setImages(galleryImages);
        }

        // ...rest of function unchanged...

        setIsLoading(false);
        setVenueLoaded(true);
        return r;
      })
      .catch(error => {
        setIsLoading(false);
        navigate('/error/404');
        return null;
      });
  };

  // Function to fetch meeting rooms
  const fetchMeetingRooms = (selectedDate = null) => {
    setLoadingMeetingRooms(true);

    const params = {
      booking_type: 'MR',
      date: selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined
    };

    getVenueResources(slug, params)
      .then(response => {
        setMeetingRooms(response.resources || []);
        setLoadingMeetingRooms(false);
        initialLoads.current.meetingRooms = true;
      })
      .catch(error => {
        console.error('Error fetching meeting rooms:', error);
        setLoadingMeetingRooms(false);
      });
  };

  // Function to fetch hot desk prices
  const fetchHotDeskPrices = (selectedDate = null) => {
    setLoadingHotDesks(true);

    // For hot desks, we need prices for the entire month
    const startDate = selectedDate
      ? selectedDate.startOf('month').startOf('week')
      : dayjs().startOf('month').startOf('week');

    const endDate = selectedDate
      ? selectedDate.endOf('month').endOf('week')
      : dayjs().endOf('month').endOf('week');

    const params = {
      booking_type: 'HD',
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD')
    };

    getVenuePrice(slug, params)
      .then(response => {
        setHotDeskPrices(response.prices || []);

        // If we have a selectedDate, find and set the price for that day
        if (selectedDate && response.prices && response.prices.length > 0) {
          const dateStr = selectedDate.format('YYYY-MM-DD');
          const matchingPrice = response.prices.find(p =>
            dayjs(p.start).format('YYYY-MM-DD') === dateStr
          );

          if (matchingPrice) {
            setSelectedPrice(matchingPrice);
          }
        }

        setLoadingHotDesks(false);
        initialLoads.current.hotDesks = true;
      })
      .catch(error => {
        console.error('Error fetching hot desk prices:', error);
        setLoadingHotDesks(false);
      });
  };

  // Function to fetch private offices (if needed)
  const fetchPrivateOffices = () => {
    // Similar to fetchMeetingRooms but for private offices (type: 'ST')
  };

  // Function to fetch resources based on booking type
  const fetchResourcesByType = (type, date = null) => {
    switch (type) {
      case 'MR':
        fetchMeetingRooms(date);
        break;
      case 'HD':
        fetchHotDeskPrices(date);
        break;
      case 'ST': // Private offices
        fetchPrivateOffices();
        break;
      default:
        console.warn("Unknown booking type:", type);
    }
  };

  // Handler for booking type change
  const handleBookingTypeChange = (newBookingType) => {
    scrollToStickyPosition();

    // Reset selected resource and price when changing types
    setSelectedResource(null);
    setSelectedPrice(null);

    // Update the booking type in the context
    setBookingType(newBookingType);

    // Update the URL to include the new booking type (preserve date if present)
    const dateQueryParam = selectedDate ? `date=${selectedDate.format('YYYY-MM-DD')}` : '';
    const typeQueryParam = `type=${newBookingType}`;

    // Build the new URL
    let newURL = `/venue/${slug}?`;
    if (dateQueryParam) {
      newURL += `${dateQueryParam}&${typeQueryParam}`;
    } else {
      newURL += typeQueryParam;
    }

    // Update URL without page reload
    navigate(newURL, { replace: true });

    // Fetch resources for the new booking type
    fetchResourcesByType(newBookingType, selectedDate);

  };

  // Handler for date selection
  const handleDateSelect = (priceObject) => {
    // Only update URL and state if the selected date is actually different
    const newDateStr = dayjs(priceObject?.start).format('YYYY-MM-DD');
    const currentDateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;

    if (newDateStr !== currentDateStr) {
      // Build URL with date and booking type
      let newURL = `/venue/${slug}?date=${newDateStr}`;
      if (bookingType) {
        newURL += `&type=${bookingType}`;
      }

      // Update the URL without reloading the page
      navigate(newURL);

      // Update selected date
      const newDate = dayjs(priceObject.start);
      setSelectedDate(newDate);

      // For hot desks, update the price directly
      if (bookingType === 'HD') {
        setSelectedPrice(priceObject);
      }

      // For other booking types, fetch resources for the new date
      if (bookingType !== 'HD') {
        fetchResourcesByType(bookingType, newDate);
      }
    }
  };

  // Handler for date change from other components
  const handleDateChangeFromResources = (newDate) => {
    if (!selectedDate || (newDate && newDate.format('YYYY-MM-DD') !== selectedDate.format('YYYY-MM-DD'))) {
      setSelectedDate(newDate);

      // Update the URL with the new date
      const newURL = `/venue/${slug}?date=${newDate.format('YYYY-MM-DD')}` +
        (bookingType ? `&type=${bookingType}` : '');
      navigate(newURL);

      // Fetch resources for the new date
      fetchResourcesByType(bookingType, newDate);
    }
  };

  // Handler for resource selection for details view
  const handleResourceSelected = (resource) => {
    setSelectedResource(resource);
  };

  const handleResourceView = (resource) => {
    setResourceToView(resource);
  }

  // Handler for resource modal closing
  const handleResourceModalClosed = () => {
    setResourceToView(null);
  };

  // Helper function for day names
  const getDayName = (day) => {
    switch (day) {
      case 0: return 'Monday';
      case 1: return 'Tuesday';
      case 2: return 'Wednesday';
      case 3: return 'Thursday';
      case 4: return 'Friday';
      case 5: return 'Saturday';
      case 6: return 'Sunday';
      default: return '';
    }
  };

  // Function to handle booking creation after login (if needed)
  const handlePendingBooking = () => {
    const pendingData = sessionStorage.getItem('pendingBookingData');
    if (pendingData) {
      try {
        const bookingData = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingBookingData'); // Clear the stored data
        handleBookingCreation(bookingData);
      } catch (error) {
        console.error("Error parsing pending booking data:", error);
        enqueueSnackbar(t('booking.message.error'), {
          variant: 'error',
        });
      }
    }
  };

  // Handler for successful login
  const handleLoginSuccess = () => {
    setLoggedIn(true);
    setRequestLogin(false);
    handlePendingBooking();
  };

  // Function to handle promo signup
  const handlePromoSignUp = () => {
    setShowPromoModal(false);
    setRequestLogin(true);
  };

  // Main booking function - checks login status first
  const doBooking = (bookingData) => {
    if (!loggedIn) {
      setDefaultView('login');
      setRequestLogin(true);
      // Store booking data for after login
      sessionStorage.setItem('pendingBookingData', JSON.stringify(bookingData));
      return;
    }
    handleBookingCreation(bookingData);
  };

  // Function to create booking
  const handleBookingCreation = (bookingData) => {
    setPreparingBooking(true);

    // Format the dates and create booking payload
    const {
      bookingType,
      resource,
      date,
      timeSlot,
      spaces,
      totalPrice,
      totalPriceVAT,
      communitySlug
    } = bookingData;

    // Format date string
    const startDateTime = date ? date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

    // Determine resource or work area slug based on booking type
    let resourceSlug, workAreaSlug;
    if (bookingType === 'MR') {
      resourceSlug = resource?.slug;
      workAreaSlug = null;
    } else if (bookingType === 'HD') {
      resourceSlug = null;
      workAreaSlug = resource?.workAreaSlug || resource?.slug;
    }

    const postData = {
      "bookings": [
        {
          booking_type: bookingType,
          venue: slug,
          resource: resourceSlug,
          work_area: workAreaSlug,
          start: startDateTime,
          // Include time information if it exists
          ...(timeSlot?.start_time && { start_time: timeSlot.start_time }),
          ...(timeSlot?.end_time && { end_time: timeSlot.end_time }),
          // Include number of spaces for hot desks
          ...(bookingType === 'HD' && { number_spaces: spaces }),
          // Use the price with VAT for the final price
          price: totalPriceVAT,
          instant_booking: selectedPrice?.instant_booking || true,
          // Include group if a community was selected
          ...(communitySlug && { group: communitySlug }),
        }
      ]
    };


    createBooking(postData)
      .then(r => {
        setSessionId(r.sessionId);
      })
      .catch(error => {
        console.error("Booking creation error:", error);
        setPreparingBooking(false);
        enqueueSnackbar(t('booking.message.error'), {
          variant: 'error',
        });
      });
  };

  useEffect(() => {
  }, [selectedResource]);

  // Add this effect to handle the initial booking type setting
  useEffect(() => {
    // This effect should run when venue data is loaded and we know which types are enabled
    if (venue && !bookingTypeParam) {

      // Choose the first available type between HD and MR
      if (enabledHD) {
        setBookingType('HD');
      } else if (enabledMR) {
        setBookingType('MR');
      } else {
        // Fallback to the first available booking type from the venue
        const firstAvailableType = venue?.available_booking_types?.[0] || 'HD';
        setBookingType(firstAvailableType);
      }

      // Update the URL to include the selected booking type
      const dateQueryParam = selectedDate ? `date=${selectedDate.format('YYYY-MM-DD')}` : '';
      const typeQueryParam = `type=${bookingType}`;

      let newURL = `/venue/${slug}?`;
      if (dateQueryParam) {
        newURL += `${dateQueryParam}&${typeQueryParam}`;
      } else {
        newURL += typeQueryParam;
      }

      navigate(newURL, { replace: true });
    }
  }, [venue, enabledHD, enabledMR, bookingTypeParam]);

  // Effect for handling Stripe checkout redirection
  useEffect(() => {
    if (sessionId) {
      const redirectToCheckout = async () => {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: sessionId,
        });
        setPreparingBooking(false);
        if (error) {
          enqueueSnackbar(t('booking.message.checkout_error'), {
            variant: 'error',
          });
        }
      };
      redirectToCheckout();
    }
  }, [sessionId, enqueueSnackbar, t]);

  // Effect for updating total price when selected price or spaces change
  useEffect(() => {
    if (selectedPrice) {
      if (selectedDate == null) setSelectedDate(dayjs(selectedPrice?.start));

      // Calculate total price based on available spaces
      if (selectedPrice && selectedPrice?.available < spaces) {
        setTotalPrice(0);
        setTotalPriceVAT(0);
      } else {
        const dailyPrice = selectedPrice ? parseFloat(selectedPrice?.price) : 0;
        const dailyPriceVAT = selectedPrice ? parseFloat(selectedPrice?.price_vat) : 0;
        setTotalPrice(dailyPrice * spaces);
        setTotalPriceVAT(dailyPriceVAT * spaces);
      }
    } else {
      setTotalPrice(0);
      setTotalPriceVAT(0);
    }
  }, [spaces, selectedPrice, selectedDate]);

  // Effect to initialize booking type from URL
  useEffect(() => {
    if (bookingTypeParam) {
      const validBookingTypes = ['HD', 'MR', 'ST']; // Hot Desk, Meeting Room, Studio/Private Office
      if (validBookingTypes.includes(bookingTypeParam.toUpperCase())) {
        setBookingType(bookingTypeParam.toUpperCase());
      }
    }
  }, [bookingTypeParam, setBookingType]);

  // Initial load effect
  useEffect(() => {

    // Check if we should show promo modal
    if (communityParam === 'never-work-alone') {
      setShowPromoModal(true);
    }

    // Load venue information
    getVenueInfo();
  }, []);

  // Effect to load resources when venue is loaded and booking type is set
  useEffect(() => {
    if (venue && bookingType) {
      fetchResourcesByType(bookingType, selectedDate);
    }
  }, [venue, bookingType]);

  // Get the resources for the current booking type
  const getCurrentResources = () => {
    switch (bookingType) {
      case 'MR':
        return meetingRooms;
      case 'HD':
        return hotDeskAreas;
      case 'ST':
        return privateOffices;
      default:
        return [];
    }
  };

  // Check if resources are loading
  const isLoadingResources = () => {
    switch (bookingType) {
      case 'MR':
        return loadingMeetingRooms;
      case 'HD':
        return loadingHotDesks;
      default:
        return false;
    }
  };

  return (
    <VenueContainer>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: '100%',
            position: 'fixed',
            top: 0,
            left: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12} sx={{ pt: 2 }}>
            <OverviewMainTitle>{venue?.name}</OverviewMainTitle>
          </Grid>

          {/* Gallery */}
          <Grid item xs={12}>
            <ContentContainer>
              <GalleryGrid images={images} />
            </ContentContainer>
          </Grid>

          {/* Mobile Booking Creator */}
          {isMobile && <Grid item xs={12}>
            <StickyContainer>
              <BookingCreator
                slug={slug}
                venue={venue}
                defaultDate={selectedDate}
                onBookingComplete={doBooking}
                availableBookingTypes={venue?.available_booking_types || ['HD', 'MR']}
                initialBookingType={bookingType}
                onBookingTypeChange={handleBookingTypeChange}
                resources={getCurrentResources()}
                loadingResources={isLoadingResources()}
                onDateChange={handleDateChangeFromResources}
                preparingBooking={preparingBooking}
                spaces={spaces}
                selectedPrice={selectedPrice}
                totalPrice={totalPrice}
                totalPriceVAT={totalPriceVAT}
                compactView={true}
                viewResourceDetails={handleResourceView}
                externalSelectedResource={selectedResource}
                enabledHD={enabledHD}
                enabledMR={enabledMR}
                defaultCommunity={communityParam}
              />
            </StickyContainer>
          </Grid>}

          {/* Main content area */}
          <Grid item xs={12} md={8}>
            <GeneralInfo
              venue={venue}
              slug={slug}
              amenities={amenities}
              venueResources={getCurrentResources()}
              loadingResources={isLoadingResources()}
              bookingType={bookingType}
              selectedDate={selectedDate}
              onDateChange={handleDateChangeFromResources}
              initialBookingType={bookingType}
              onBookingTypeChange={handleBookingTypeChange}
              onResourceModalClose={handleResourceModalClosed}
              resourceToView={resourceToView}
              setSelectedResource={(resource) => {
                if (!resource) return; // Guard against null


                // Force a new reference by creating a completely new object
                const newResource = {
                  ...resource,
                  _timestamp: Date.now() // Add a unique field to guarantee reference change
                };

                setSelectedResource(newResource);
              }}
            />
          </Grid>

          {/* Desktop Booking Creator */}
          {!isMobile && <Grid item md={4}>
            <StickyContainer>
              <BookingCreator
                slug={slug}
                venue={venue}
                defaultDate={selectedDate}
                onBookingComplete={doBooking}
                availableBookingTypes={venue?.available_booking_types || ['HD', 'MR']}
                initialBookingType={bookingType}
                onBookingTypeChange={handleBookingTypeChange}
                resources={getCurrentResources()}
                loadingResources={isLoadingResources()}
                onDateChange={handleDateChangeFromResources}
                preparingBooking={preparingBooking}
                spaces={spaces}
                selectedPrice={selectedPrice}
                totalPrice={totalPrice}
                totalPriceVAT={totalPriceVAT}
                compactView={true}
                viewResourceDetails={handleResourceView}
                externalSelectedResource={selectedResource}
                enabledHD={enabledHD}
                enabledMR={enabledMR}
                defaultCommunity={communityParam}
              />
            </StickyContainer>
          </Grid>}
        </Grid>


      )}

      {/* Login Modal */}
      <Modal
        open={requestLogin}
        onClose={() => setRequestLogin(false)}
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
        disableAutoFocus={true}
      >
        <ModalContent>
          <Box sx={{
            width: '100%',
            maxWidth: '450px',
            minWidth: { xs: '90vw', sm: '450px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent'
          }}>
            <Login
              isModal={true}
              onLoginSuccess={handleLoginSuccess}
              onClose={() => setRequestLogin(false)}
              postAction={{ action: 'addBooking', 'group': selectedCommunity }}
              defaultView={defaulView}
            />
          </Box>
        </ModalContent>
      </Modal>

      {/* Promo Modal */}
      <Modal
        open={showPromoModal && !loggedIn}
        onClose={() => setShowPromoModal(false)}
        aria-labelledby="promo-modal-title"
        aria-describedby="promo-modal-description"
        disableAutoFocus={true}
      >
        <PromoContent>
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent'
          }}>
            <PromoModalContent
              onClose={() => setShowPromoModal(false)}
              onSignUp={handlePromoSignUp}
            />
          </Box>
        </PromoContent>
      </Modal>
    </VenueContainer>
  );
};

export default VenueSingle;